import { Link, Navigate, useLocation, useNavigate, useParams, type LinkProps } from "react-router-dom";

// The candidate card is a full-screen modal over whatever page opened it:
// `?candidate=<id>` on the current URL, rendered by CandidateModalHost in the
// shell. The page underneath stays mounted, and Back closes the card.
export const CANDIDATE_PARAM = "candidate";

/** Marks a history entry pushed by opening the card, so closing can go Back. */
export type CandidateModalState = { candidateModal?: boolean } | null;

export const OPENED: CandidateModalState = { candidateModal: true };

export function withCandidate(pathname: string, search: string, id: string) {
  const sp = new URLSearchParams(search);
  sp.set(CANDIDATE_PARAM, id);
  return { pathname, search: `?${sp}` };
}

export function CandidateLink({ id, ...rest }: Omit<LinkProps, "to" | "state"> & { id: string }) {
  const { pathname, search } = useLocation();
  return <Link {...rest} to={withCandidate(pathname, search, id)} state={OPENED} />;
}

export function useOpenCandidate() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  return (id: string) => navigate(withCandidate(pathname, search, id), { state: OPENED });
}

/** `/candidates/<id>` (extension, old links) → the list with the card open. */
export function CandidateRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={withCandidate("/candidates", "", id)} replace />;
}

/** App path of a candidate card, for callers outside the router tree. */
export function candidateIdFromPath(path: string): string | null {
  const m = /^\/candidates\/([^/?#]+)/.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
}
